import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";
import { Briefcase, Users, FileText, ClipboardCheck, Clock, Trophy, Award, ArrowRight, TrendingUp } from "lucide-react";

const Kpi = ({ icon: I, label, value, accent="text-[#F59E0B]", testid }) => (
  <Card className="bg-slate-900 border-slate-800 p-5 text-white card-hover" data-testid={testid}>
    <div className="flex items-center justify-between">
      <I className={`w-5 h-5 ${accent}`}/>
      <TrendingUp className="w-3.5 h-3.5 text-slate-600"/>
    </div>
    <div className="mt-6 font-display text-3xl font-bold">{value ?? 0}</div>
    <div className="text-xs uppercase tracking-wider text-slate-400 mt-1">{label}</div>
  </Card>
);

export default function AdminDashboard() {
  const [k, setK] = useState({});
  const [actions, setActions] = useState([]);
  const [health, setHealth] = useState(null);
  const [funnel, setFunnel] = useState(null);
  useEffect(()=>{
    api.get("/admin/kpis").then(r=>setK(r.data));
    api.get("/admin/action-center").then(r=>setActions(r.data.items || []));
    api.get("/admin/system-health").then(r=>setHealth(r.data)).catch(()=>{});
    api.get("/admin/analytics/funnel").then(r=>setFunnel(r.data)).catch(()=>{});
  },[]);
  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F59E0B]">Overview</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white mt-1">VS Management Dashboard</h1>
          <p className="text-slate-400 mt-1">Live recruitment metrics for VS-Solutions Careers.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/jd-generator" className="text-sm text-[#F59E0B] flex items-center gap-1 hover:underline border border-[#F59E0B]/30 rounded-md px-3 py-1.5" data-testid="admin-jd-gen-link">JD Generator</Link>
          <Link to="/admin/applications" className="text-sm text-[#F59E0B] flex items-center gap-1 hover:underline">View applications <ArrowRight className="w-3 h-3"/></Link>
        </div>
      </div>

      {actions.length > 0 && (
        <Card className="bg-slate-900 border-slate-800 p-6 mb-6" data-testid="action-center">
          <div className="text-xs uppercase tracking-[0.2em] text-[#F59E0B] font-semibold">Requires Your Attention</div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {actions.map(a => (
              <Link key={a.key} to={a.link} className="border border-slate-800 rounded-md p-4 hover:border-[#F59E0B]/50 transition-colors" data-testid={`action-${a.key}`}>
                <div className="font-display text-2xl font-bold text-white">{a.count}</div>
                <div className="text-xs text-slate-400 mt-1">{a.label}</div>
                <div className="text-[10px] text-[#F59E0B] mt-2 uppercase tracking-wider">Review →</div>
              </Link>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        <Kpi icon={Users} label="Candidates" value={k.total_candidates} testid="admin-kpi-candidates"/>
        <Kpi icon={Briefcase} label="Active Jobs" value={k.active_jobs} accent="text-[#2563EB]" testid="admin-kpi-jobs"/>
        <Kpi icon={FileText} label="Total Applications" value={k.total_applications} testid="admin-kpi-total-apps"/>
        <Kpi icon={FileText} label="Applications Today" value={k.applications_today} accent="text-[#2563EB]" testid="admin-kpi-apps-today"/>
        <Kpi icon={ClipboardCheck} label="Asmt Completed" value={k.assessments_completed} accent="text-emerald-500" testid="admin-kpi-asmt-done"/>
        <Kpi icon={Clock} label="Asmt Pending" value={k.assessments_pending} testid="admin-kpi-asmt-pending"/>
        <Kpi icon={Clock} label="Under Review" value={k.under_review} accent="text-[#2563EB]" testid="admin-kpi-under-review"/>
        <Kpi icon={Trophy} label="Shortlisted" value={k.shortlisted} accent="text-emerald-500" testid="admin-kpi-shortlisted"/>
        <Kpi icon={Award} label="Interviews" value={k.interviews} accent="text-purple-400" testid="admin-kpi-interviews"/>
        <Kpi icon={Award} label="Offers" value={k.offers} accent="text-pink-400" testid="admin-kpi-offers"/>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-8">
        <Card className="bg-slate-900 border-slate-800 p-6">
          <h3 className="font-display text-lg font-semibold text-white">System Health</h3>
          <div className="mt-4 space-y-2 text-sm">
            {health ? [
              ["API", health.api?.status],
              ["Database", health.database?.status],
              ["Email Service", health.email?.status],
              ["AI Service", health.ai?.status],
              ["Notifications", health.notifications?.status],
            ].map(([label, status])=> (
              <div key={label} className="flex items-center justify-between border-b border-slate-800 pb-2 last:border-0">
                <span className="text-slate-300">{label}</span>
                <span className={`text-xs uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${
                  status === "healthy" ? "bg-emerald-900/40 text-emerald-300" :
                  status === "warning" ? "bg-amber-900/40 text-amber-300" :
                  "bg-red-900/40 text-red-300"
                }`}>{status || "—"}</span>
              </div>
            )) : <p className="text-slate-500 text-sm">Loading health status…</p>}
            {health?.email?.failures_24h > 0 && <p className="text-xs text-amber-400 mt-2">{health.email.failures_24h} email failures in the last 24h.</p>}
          </div>
        </Card>
        <Card className="bg-slate-900 border-slate-800 p-6">
          <h3 className="font-display text-lg font-semibold text-white">Hiring funnel</h3>
          <div className="mt-4 space-y-2 text-sm text-slate-300">
            {(funnel?.funnel || [
              {stage:"Applications", count:k.total_applications},
              {stage:"Assessment Completed", count:k.assessments_completed},
              {stage:"Under Review", count:k.under_review},
              {stage:"Shortlisted", count:k.shortlisted},
              {stage:"Interviews", count:k.interviews},
              {stage:"Offers", count:k.offers},
            ]).map((row, idx)=>{
              const max = Math.max(1, ...(funnel?.funnel || []).map(x=>x.count||0), k.total_applications||1);
              const pct = Math.min(100, ((row.count||0)*100)/max);
              const colors = ["bg-slate-500","bg-[#2563EB]","bg-emerald-500","bg-[#F59E0B]","bg-purple-500","bg-pink-500","bg-white"];
              return <div key={row.stage}>
                <div className="flex justify-between"><span>{row.stage}</span><span className="font-semibold">{row.count||0}</span></div>
                <div className="h-1.5 bg-slate-800 rounded-full mt-1"><div className={`h-full rounded-full ${colors[idx%colors.length]}`} style={{width:`${pct}%`}}/></div>
              </div>;
            })}
          </div>
        </Card>
      </div>

      {funnel && (funnel.by_domain?.length > 0 || funnel.by_location?.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          <Card className="bg-slate-900 border-slate-800 p-6">
            <h3 className="font-display text-lg font-semibold text-white mb-4">Applications by Technology</h3>
            {funnel.by_domain.length ? funnel.by_domain.map(d => {
              const max = funnel.by_domain[0].count || 1;
              return (
                <div key={d.domain} className="mb-3">
                  <div className="flex justify-between text-sm text-slate-300"><span>{d.domain}</span><span className="font-semibold">{d.count}</span></div>
                  <div className="h-1.5 bg-slate-800 rounded-full mt-1"><div className="h-full rounded-full bg-[#2563EB]" style={{width:`${(d.count*100)/max}%`}}/></div>
                </div>
              );
            }) : <p className="text-sm text-slate-500">No application data yet.</p>}
          </Card>
          <Card className="bg-slate-900 border-slate-800 p-6">
            <h3 className="font-display text-lg font-semibold text-white mb-4">Applications by Location</h3>
            {funnel.by_location.length ? funnel.by_location.map(d => {
              const max = funnel.by_location[0].count || 1;
              return (
                <div key={d.location} className="mb-3">
                  <div className="flex justify-between text-sm text-slate-300"><span>{d.location}</span><span className="font-semibold">{d.count}</span></div>
                  <div className="h-1.5 bg-slate-800 rounded-full mt-1"><div className="h-full rounded-full bg-[#F59E0B]" style={{width:`${(d.count*100)/max}%`}}/></div>
                </div>
              );
            }) : <p className="text-sm text-slate-500">No application data yet.</p>}
          </Card>
        </div>
      )}
    </AdminLayout>
  );
}
